import React, { useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import color from "../../Constants/colors";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import { ComoSwitch } from "../Common/ComoSwitch";
import {
  useMyScheduledTasks,
  useToggleMyScheduledTask,
  useDeleteMyScheduledTask,
} from "../../Query/useScheduledTask";
import {
  ScheduledTask,
  ScheduleType,
  ScheduleSource,
} from "../../Models/scheduledTask";

interface MyScheduledTasksProps {
  onClose?: () => void;
  onOpenConversation?: (conversationId: number) => void;
}

const iconBtnSx = {
  color: color.gray500,
  "&:hover": {
    bgcolor: color.gray100,
    color: color.accent,
  },
};

const MyScheduledTasks: React.FC<MyScheduledTasksProps> = ({
  onClose,
  onOpenConversation,
}) => {
  const { t } = useTranslation("scheduledTasks");
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detailTask, setDetailTask] = useState<ScheduledTask | null>(null);

  const { data, isLoading, isError } = useMyScheduledTasks();
  const { mutate: toggleTask } = useToggleMyScheduledTask();
  const { mutate: deleteTask } = useDeleteMyScheduledTask();

  const tasks: ScheduledTask[] = data ?? [];

  const describeSchedule = (task: ScheduledTask): string => {
    switch (task.schedule_type) {
      case ScheduleType.ONCE:
        return t("scheduleOnce", { time: task.run_at ?? "—" });

      case ScheduleType.INTERVAL:
        return t("scheduleEvery", { seconds: task.interval_seconds ?? "?" });

      case ScheduleType.CRON:
        return t("scheduleCron", { expression: task.cron_expression ?? "—" });

      default:
        return t("unknown");
    }
  };

  const describeWork = (task: ScheduledTask): string => {
    const prompt = task.kwargs?.["prompt"];
    if (typeof prompt === "string" && prompt.trim()) return prompt;
    return task.task_name;
  };

  const handleConfirmDelete = (confirmed: boolean) => {
    setConfirmationDialogOpen(false);
    if (confirmed && selectedId) {
      deleteTask(selectedId);
    }
  };

  const detailRow = (label: string, value: React.ReactNode) => (
    <Box sx={{ display: "flex", gap: 2, py: 0.75 }}>
      <Typography
        variant="caption"
        sx={{
          color: color.gray500,
          minWidth: 130,
          flexShrink: 0,
          textTransform: "uppercase",
          fontSize: "0.65rem",
          fontWeight: 600,
          mt: 0.25,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, flex: 1 }}>{value}</Box>
    </Box>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: color.accent }} />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          maxWidth: 960,
          mx: "auto",
          px: { xs: 2, md: 4 },
          py: 4,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <ScheduleIcon
              sx={{ color: color.accent, fontSize: 28, flexShrink: 0, mt: 0.25 }}
            />

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: color.primary_dark }}
              >
                {t("myTitle")}
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: color.gray500, mt: 0.25 }}
              >
                {t("mySubtitle")}
              </Typography>
            </Box>
          </Box>

          {onClose && (
            <IconButton
              onClick={onClose}
              sx={iconBtnSx}
              aria-label={t("close")}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        {isError ? (
          <Typography sx={{ color: color.red700 }}>{t("errorFetch")}</Typography>
        ) : tasks.length === 0 ? (
          <Typography sx={{ color: color.gray500 }}>
            {t("myEmptyState")}
          </Typography>
        ) : (
          <Box
            sx={{
              border: `1px solid ${color.gray200}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            {tasks.map((task) => (
              <Box
                key={task.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  alignItems: "center",
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${color.gray100}`,
                  "&:last-of-type": { borderBottom: "none" },
                  "&:hover": { backgroundColor: color.gray50 },
                  opacity: task.enabled ? 1 : 0.6,
                }}
              >
                <Box
                  onClick={() => setDetailTask(task)}
                  sx={{
                    minWidth: 0,
                    cursor: "pointer",
                    "&:hover .task-name": { color: color.accent },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      className="task-name"
                      sx={{
                        fontWeight: 600,
                        color: color.primary_dark,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {task.name}
                    </Typography>

                    <Chip
                      size="small"
                      label={
                        task.source === ScheduleSource.AGENT
                          ? task.agent_name || t("agent")
                          : t("admin")
                      }
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        flexShrink: 0,
                        bgcolor:
                          task.source === ScheduleSource.AGENT
                            ? color.green50
                            : color.gray100,
                        color:
                          task.source === ScheduleSource.AGENT
                            ? color.green700
                            : color.gray600,
                      }}
                    />
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: color.gray600,
                      mt: 0.25,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {describeWork(task)}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                      mt: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: color.gray500, fontSize: "0.7rem" }}
                    >
                      {describeSchedule(task)}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{ color: color.gray400, fontSize: "0.7rem" }}
                    >
                      ·
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{ color: color.gray500, fontSize: "0.7rem" }}
                    >
                      {t("nextRun")}: {task.next_run_at ?? "—"}
                    </Typography>

                    {task.last_status && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.7rem",
                          color:
                            task.last_status === "error"
                              ? color.red700
                              : color.green700,
                        }}
                      >
                        {task.last_status}
                        {task.run_count ? ` (${task.run_count}x)` : ""}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <Tooltip
                    title={task.can_manage ? t("enabled") : t("readOnlyHint")}
                    placement="top"
                  >
                    <span>
                      <ComoSwitch
                        size="small"
                        checked={task.enabled}
                        disabled={!task.can_manage}
                        onChange={(e) =>
                          toggleTask({ id: task.id, enabled: e.target.checked })
                        }
                      />
                    </span>
                  </Tooltip>

                  {task.conversation_id && onOpenConversation && (
                    <Tooltip title={t("openConversation")} placement="top">
                      <IconButton
                        size="small"
                        onClick={() =>
                          onOpenConversation(task.conversation_id as number)
                        }
                        sx={iconBtnSx}
                      >
                        <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}

                  {task.can_manage ? (
                    <Tooltip title={t("delete")} placement="top">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedId(task.id);
                          setConfirmationDialogOpen(true);
                        }}
                        sx={iconBtnSx}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title={t("readOnlyHint")} placement="top">
                      <LockOutlinedIcon
                        sx={{ fontSize: 18, color: color.gray400, mx: 1 }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Dialog
        open={Boolean(detailTask)}
        onClose={() => setDetailTask(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "14px",
            border: `1px solid ${color.gray200}`,
          },
        }}
      >
        {detailTask && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                pb: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: color.primary_dark }}
                >
                  {detailTask.name}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  <Chip
                    size="small"
                    label={
                      detailTask.source === ScheduleSource.AGENT
                        ? detailTask.agent_name || t("agent")
                        : t("admin")
                    }
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      bgcolor:
                        detailTask.source === ScheduleSource.AGENT
                          ? color.green50
                          : color.gray100,
                      color:
                        detailTask.source === ScheduleSource.AGENT
                          ? color.green700
                          : color.gray600,
                    }}
                  />

                  <Typography
                    variant="caption"
                    sx={{
                      color: detailTask.enabled
                        ? color.green700
                        : color.gray500,
                      fontSize: "0.7rem",
                    }}
                  >
                    {detailTask.enabled ? t("active") : t("paused")}
                  </Typography>
                </Box>
              </Box>

              <IconButton
                onClick={() => setDetailTask(null)}
                sx={iconBtnSx}
                aria-label={t("close")}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2 }}>
              {detailRow(
                t("whatItRuns"),
                <Typography
                  variant="body2"
                  sx={{
                    color: color.primary_dark,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {describeWork(detailTask)}
                </Typography>,
              )}

              {detailRow(
                t("schedule"),
                <Typography variant="body2" sx={{ color: color.gray700 }}>
                  {describeSchedule(detailTask)}
                </Typography>,
              )}

              {detailRow(
                t("nextRun"),
                <Typography variant="body2" sx={{ color: color.gray700 }}>
                  {detailTask.next_run_at ?? "—"}
                </Typography>,
              )}

              {detailRow(
                t("lastRun"),
                <Typography variant="body2" sx={{ color: color.gray700 }}>
                  {detailTask.last_run_at ?? t("never")}
                  {detailTask.run_count ? ` (${detailTask.run_count}x)` : ""}
                </Typography>,
              )}

              {detailTask.last_status &&
                detailRow(
                  t("status"),
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          detailTask.last_status === "error"
                            ? color.red700
                            : color.green700,
                      }}
                    >
                      {detailTask.last_status}
                    </Typography>

                    {detailTask.last_error && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.5,
                          color: color.red700,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {detailTask.last_error}
                      </Typography>
                    )}
                  </Box>,
                )}

              {!detailTask.can_manage &&
                detailRow(
                  "",
                  <Typography variant="caption" sx={{ color: color.gray500 }}>
                    {t("readOnlyHint")}
                  </Typography>,
                )}
            </DialogContent>
          </>
        )}
      </Dialog>

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={handleConfirmDelete}
        title={t("deleteTitle")}
        description={t("deleteDescription")}
        content={t("deleteContent")}
        actions={[
          { label: t("no"), onClick: () => handleConfirmDelete(false) },
          { label: t("yes"), onClick: () => handleConfirmDelete(true) },
        ]}
      />
    </>
  );
};

export default MyScheduledTasks;
